import {
  type PaidPlanType,
  type PlanPaymentIntent,
} from '@/module/payment/shared/constants/paymentIntent';
import { type PlanType } from '@/types';

import { type UpsellingPlanState } from './upsellingPlanState';

export type { PaidPlanType };
export type UpsellingPurchaseIntent = Extract<
  PlanPaymentIntent,
  'NEW_PURCHASE' | 'FUTURE_PURCHASE'
>;

/**
 * 카드 주 버튼이 하는 일.
 *
 * 문구·비활성·클릭 동작이 모두 이 하나의 값에서 파생된다. 액션을 추가하면
 * 문구 매핑(`Record<UpsellingCtaAction, string>`)이 컴파일 에러로 누락을 잡는다.
 */
export type UpsellingCtaAction =
  /** 신규 구매 결제 플로우 */
  | 'purchase'
  /** 플랜 선택 모달을 먼저 띄운다(단체 워크스페이스) */
  | 'select-plan'
  /** `/checkout/extend` — 이용 기간 연장 */
  | 'extend'
  /** `/checkout/upgrade` — Basic → Pro */
  | 'upgrade'
  /** 상위 플랜 이용 중이라 이 카드는 선택할 수 없다 */
  | 'higher-plan-active'
  /** 이미 이 플랜을 쓰고 있다 */
  | 'current-plan'
  /** 레거시 문의 링크. 소속·과금 유형을 모를 때만 쓴다 */
  | 'inquiry';

export type UpsellingCardCta = {
  action: UpsellingCtaAction;
  disabled: boolean;
  /**
   * 주 버튼 아래 보조 "구매하기" 링크 노출 여부.
   *
   * 이용 중인 사용자는 연장·업그레이드가 기본 동선이지만 신규 구매 경로도 열어 둔다
   * (Figma 14763:36411 · 14763:36494).
   */
  hasSecondaryPurchase: boolean;
};

const DISABLED_ACTIONS = new Set<UpsellingCtaAction>([
  'current-plan',
  'higher-plan-active',
]);

const toCta = (
  action: UpsellingCtaAction,
  hasSecondaryPurchase = false,
): UpsellingCardCta => ({
  action,
  disabled: DISABLED_ACTIONS.has(action),
  hasSecondaryPurchase,
});

export const resolveInitialPlanType = ({
  currentPlan,
  initialPlanType,
}: {
  currentPlan?: PlanType;
  initialPlanType?: PaidPlanType;
}): PaidPlanType => {
  if (initialPlanType) return initialPlanType;
  return currentPlan === 'BASIC' || currentPlan === 'PRO' ? 'PRO' : 'BASIC';
};

/**
 * 카드 한 장의 CTA 를 판정한다.
 *
 * 최종 결제 허용 여부는 Checkout 과 서버가 다시 검증하므로, 여기서는 화면을 가르는
 * 것까지만 한다. 잘못 판정해도 결제로 새지 않고 되돌려진다.
 */
export const getUpsellingCardCta = ({
  state,
  targetPlan,
  intent,
}: {
  state: UpsellingPlanState;
  targetPlan: PaidPlanType;
  intent: UpsellingPurchaseIntent;
}): UpsellingCardCta => {
  if (state.kind === 'restricted') return toCta('select-plan');

  // 미래 구매는 이용 중 여부와 무관하게 항상 신규 구매다 — 현재와 같은 등급도 살 수 있다.
  if (intent === 'FUTURE_PURCHASE') return toCta('purchase');

  switch (state.kind) {
    case 'anonymous':
    case 'free':
      return toCta('purchase');

    case 'purchase-only':
      return toCta('purchase');

    case 'individual':
      if (state.tier === 'BASIC') {
        if (targetPlan === 'BASIC') return toCta('extend', true);
        return toCta(state.selfServicePurchase ? 'upgrade' : 'inquiry', true);
      }
      // Pro 이용 중이면 Basic 은 내려갈 수 없는 하위 등급이라 선택 자체를 막는다.
      return toCta(
        targetPlan === 'PRO' ? 'extend' : 'higher-plan-active',
        true,
      );

    // 서버가 소속·과금 유형을 아직 안 내려주는 구간. 등급만 보던 기존 동작을 그대로
    // 유지해, 필드가 도착하기 전에 유료 사용자 화면이 바뀌지 않게 한다.
    case 'unknown':
      if (state.tier === targetPlan) return toCta('current-plan');
      if (state.tier === 'BASIC' && targetPlan === 'PRO') {
        return toCta('inquiry');
      }
      return toCta('purchase');
  }
};
